import React from "react";
import { Statistic, Message, Table, Dropdown, Input, Button, Icon, Segment } from "semantic-ui-react";
import { CLASES_RESIDUO } from "../catalogs/clasificacionResiduo";
import { estiloDeClave } from "../catalogs/semaforoColores";
import { useWaterBalanceT } from "../useWaterBalanceT";

const CATEGORIA_OPTIONS = Object.values(CLASES_RESIDUO).map((c) => ({ key: c.id, value: c.id, text: c.nombre }));

// Panel compartido entre los modos "Cargar datos y generar" y "Mi planta":
// KPIs + semáforo + clasificación del residuo (M36). El modo "No tengo
// medidores" no lo usa — ahí el semáforo es un mensaje de punto de partida,
// no un veredicto, y no exige clasificar nada (ver ModoDia0Workspace).
export function BalanceResumenPanel({ resultado, lossClassification, onChangeClasificacion, mostrarClasificacion = true }) {
  const t = useWaterBalanceT();
  const lista = lossClassification || [];
  const clasificado = lista.reduce((a, c) => a + (Number(c.volumeM3) || 0), 0);
  const faltaClasificar = Math.max(0, resultado.descuadre - clasificado);

  const actualizar = (idx, campo, val) => onChangeClasificacion(lista.map((c, i) => (i === idx ? { ...c, [campo]: val } : c)));
  const agregar = () => onChangeClasificacion([...lista, { category: "sin", volumeM3: "", note: "" }]);
  const quitar = (idx) => onChangeClasificacion(lista.filter((_, i) => i !== idx));

  return (
    <div>
      <Statistic.Group widths={3}>
        <Statistic><Statistic.Value>{resultado.totalEntradas.toLocaleString("es-AR")}</Statistic.Value><Statistic.Label>{t("water_balance_stat_entries")}</Statistic.Label></Statistic>
        <Statistic><Statistic.Value>{resultado.totalSalidas.toLocaleString("es-AR")}</Statistic.Value><Statistic.Label>{t("water_balance_stat_outputs")}</Statistic.Label></Statistic>
        <Statistic color={resultado.descuadre < 0 ? "red" : undefined}>
          <Statistic.Value>{resultado.descuadre.toLocaleString("es-AR")}</Statistic.Value>
          <Statistic.Label>{t("water_balance_stat_imbalance")} · {resultado.descuadrePct.toFixed(1)}%</Statistic.Label>
        </Statistic>
      </Statistic.Group>

      <Message icon style={{ marginTop: 16, ...estiloDeClave(resultado.semaforo.clave) }}>
        <Icon name={resultado.semaforo.severidad === "ok" ? "check circle" : resultado.semaforo.severidad === "stop" ? "times circle" : "warning circle"} />
        <Message.Content>
          <Message.Header>{resultado.semaforo.titulo}</Message.Header>
          {resultado.semaforo.detalle}
          {resultado.semaforo.notaMetodologica && <p style={{ marginTop: 8, fontStyle: "italic" }}>{resultado.semaforo.notaMetodologica}</p>}
        </Message.Content>
      </Message>

      <p><strong>{t("water_balance_quality_index_label")}</strong> {resultado.indiceCalidad.toFixed(2)} / 5</p>

      {mostrarClasificacion && resultado.descuadre > 0 && (
        <Segment>
          <h4>{t("water_balance_classification_title")}</h4>
          <p>{t("water_balance_classification_hint")}</p>
          <Table basic="very" compact>
            <Table.Body>
              {lista.map((c, idx) => (
                <Table.Row key={idx}>
                  <Table.Cell width={5}><Dropdown selection compact fluid options={CATEGORIA_OPTIONS} value={c.category} onChange={(_, d) => actualizar(idx, "category", d.value)} /></Table.Cell>
                  <Table.Cell width={3}><Input type="number" placeholder={t("water_balance_volume_placeholder")} value={c.volumeM3} onChange={(e) => actualizar(idx, "volumeM3", e.target.value === "" ? "" : Number(e.target.value))} /></Table.Cell>
                  <Table.Cell><Input placeholder={t("water_balance_note_placeholder")} value={c.note || ""} onChange={(e) => actualizar(idx, "note", e.target.value)} /></Table.Cell>
                  <Table.Cell width={1}><Button icon="trash" basic size="tiny" onClick={() => quitar(idx)} /></Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          <Button basic size="small" onClick={agregar}><Icon name="plus" /> {t("water_balance_add_classification_button")}</Button>
          <p style={{ marginTop: 10 }}>
            {t("water_balance_classified_label")} {clasificado.toLocaleString("es-AR")} m³ {t("water_balance_of_label")} {resultado.descuadre.toLocaleString("es-AR")} m³.{" "}
            {faltaClasificar > 0 ? (
              <strong style={{ color: "#c0392b" }}>{t("water_balance_missing_to_classify", { n: faltaClasificar.toLocaleString("es-AR") })}</strong>
            ) : (
              <span style={{ color: "#2e7d5b" }}>{t("water_balance_classification_complete")}</span>
            )}
          </p>
        </Segment>
      )}

      {resultado.bloqueos.lecturaInvalida && (
        <Message error icon="warning sign" content={t("water_balance_invalid_reading_message")} />
      )}

      {resultado.bloqueos.residuoSobreClasificado && (
        <Message error icon="warning sign" content={t("water_balance_over_classified_message", { vol: (clasificado - resultado.descuadre).toLocaleString("es-AR") })} />
      )}

      {resultado.bloqueos.dobleConteoInstrumentos && (
        <Message error icon="ban" content={t("water_balance_double_count_blocking_message")} />
      )}

      {resultado.advertencias?.estimacionesAltas && (
        <Message warning icon="chart bar" content={t("water_balance_high_estimates_warning")} />
      )}

      {resultado.advertencias?.sinAtribuirEnTop80 && (
        <Message warning icon="question circle" content={t("water_balance_unattributed_top_pareto_warning")} />
      )}

      <Message color={resultado.cierraOk ? "green" : "grey"} icon={resultado.cierraOk ? "check" : "lock"} style={{ marginTop: 16 }}>
        {resultado.cierraOk ? t("water_balance_can_close") : t("water_balance_cannot_close")}
      </Message>
    </div>
  );
}

export default BalanceResumenPanel;
